import { useState } from 'react';
import { Modal, Button, ListGroup, Image } from 'react-bootstrap';
import { useForm } from '@tanstack/react-form';
import { YouTubeService } from '@/services/youtube.service';
import { TrackService } from '@/services/track.service';
import type { YouTubeSearchResult } from '@/types';
import { useAuth } from '@/hooks/useAuth';

interface AddTrackModalProps {
    show: boolean;
    onHide: () => void;
    playlistId: string;
    onTrackAdded?: () => void;
}

export function AddTrackModal({ show, onHide, playlistId, onTrackAdded }: AddTrackModalProps) {
    const { user } = useAuth();
    const [results, setResults] = useState<YouTubeSearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isAdding, setIsAdding] = useState<string | null>(null);

    const searchForm = useForm({
        defaultValues: {
            query: '',
        },
        onSubmit: async ({ value }) => {
            if (!value.query.trim()) return;

            setIsSearching(true);
            try {
                const vidResults = await YouTubeService.searchVideos(value.query);
                setResults(vidResults);
            } catch (err) {
                console.error(err);
            } finally {
                setIsSearching(false);
            }
        },
    });

    const handleAdd = async (video: YouTubeSearchResult) => {
        if (!user) return;
        setIsAdding(video.videoId);

        try {
            const newTrack: any = { // Omit addedAt/lastVotedAt as service handles it
                id: video.videoId, // Use videoId as track ID to prevent duplicates
                videoId: video.videoId,
                title: video.title,
                artist: video.channelTitle, // Mapping channel to artist for now
                thumbnail: video.thumbnail,
                duration: video.duration || 0,
                addedBy: user.id,
                votes: {},
                score: 0
            };

            await TrackService.addTrack(playlistId, newTrack, user.id);
            if (onTrackAdded) onTrackAdded();
            // Don't close modal immediately, allow adding more
        } catch (err) {
            console.error('Failed to add track', err);
            alert('Failed to add track: ' + (err as any).message);
        } finally {
            setIsAdding(null);
        }
    };

    const handleClose = () => {
        searchForm.reset();
        setResults([]);
        onHide();
    }

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Add Tracks</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        searchForm.handleSubmit();
                    }}
                    className="mb-4"
                >
                    <searchForm.Field name="query">
                        {(field) => (
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search YouTube..."
                                    value={field.state.value}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    autoFocus
                                />
                                <button className="btn btn-primary" type="submit" disabled={isSearching}>
                                    {isSearching ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-search"></i>}
                                </button>
                            </div>
                        )}
                    </searchForm.Field>
                </form>

                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {results.length > 0 ? (
                        <ListGroup variant="flush">
                            {results.map(video => (
                                <ListGroup.Item key={video.videoId} className="d-flex align-items-center gap-3">
                                    <Image src={video.thumbnail} rounded style={{ width: 60, height: 45, objectFit: 'cover' }} />
                                    <div className="flex-grow-1 overflow-hidden">
                                        <h6 className="mb-0 text-truncate" title={video.title}>{video.title}</h6>
                                        <small className="text-muted">{video.channelTitle}</small>
                                    </div>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        disabled={isAdding === video.videoId}
                                        onClick={() => handleAdd(video)}
                                    >
                                        {isAdding === video.videoId ? 'Adding...' : 'Add'}
                                    </Button>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    ) : (
                        <searchForm.Subscribe selector={(state) => [state.values.query]}>
                            {([query]) => query && !isSearching && <div className="text-center text-muted py-3">No results found</div>}
                        </searchForm.Subscribe>
                    )}

                    <searchForm.Subscribe selector={(state) => [state.values.query]}>
                        {([query]) => !query && (
                            <div className="text-center text-muted py-5">
                                <i className="bi bi-youtube display-4 text-danger mb-2"></i>
                                <p>Search for videos to add to your playlist</p>
                            </div>
                        )}
                    </searchForm.Subscribe>
                </div>
            </Modal.Body>
        </Modal>
    );
}
